import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * デモモード（外部公開向けサンプルサイト）API。
 *
 * - `POST /api/demo/session` デモトークン → DRF Token 交換（認証不要、/demo/:token の入口）
 * - `GET /api/admin/demo-links` デモリンク一覧（master_admin 専用）
 * - `POST /api/admin/demo-links` デモリンク発行
 * - `GET /api/admin/demo-links/members?project_id=` 選択可能メンバー一覧
 * - `PATCH/DELETE /api/admin/demo-links/:id` 失効・期限変更・削除
 *
 * 発行時は「プロジェクト × どのメンバーとして見せるか」を指定する。
 * ロールは選んだメンバーのプロジェクト内ポジションから自動決定される。
 */

/** デモリンクの初回表示画面（BE: DemoLink.LANDING_TAB_CHOICES と対応） */
export type DemoLandingTab = "home" | "talkroom";

export interface DemoSessionResponse {
  status: string;
  data: {
    token: string;
    project_id: string;
    position: string | null;
    display_name: string;
    landing_tab: DemoLandingTab;
    expires_at: string | null;
  };
}

export interface DemoLinkProject {
  project_id: string;
  name: string;
  code_name: string;
}

export interface DemoLinkUser {
  id: number;
  display_name: string;
  position: string | null;
  avatar_url: string | null;
}

export interface DemoLink {
  demo_link_id: string;
  token: string;
  label: string;
  project: DemoLinkProject;
  demo_user: DemoLinkUser;
  landing_tab: DemoLandingTab;
  expires_at: string | null;
  is_active: boolean;
  is_available: boolean;
  access_count: number;
  last_accessed_at: string | null;
  created_at: string;
}

export interface GetDemoLinksResponse {
  status: string;
  data: {
    demo_links: DemoLink[];
  };
}

export interface CreateDemoLinkParams {
  project_id: string;
  user_id: number;
  label?: string;
  landing_tab?: DemoLandingTab;
  expires_at?: string | null;
}

export interface CreateDemoLinkResponse {
  status: string;
  data: DemoLink;
}

export interface UpdateDemoLinkParams {
  demo_link_id: string;
  label?: string;
  is_active?: boolean;
  landing_tab?: DemoLandingTab;
  expires_at?: string | null;
}

export interface DemoLinkMember {
  id: number;
  display_name: string;
  position: string | null;
  avatar_url: string | null;
  is_demo: boolean;
}

export interface GetDemoLinkMembersResponse {
  status: string;
  data: {
    project: DemoLinkProject;
    members: DemoLinkMember[];
  };
}

export const demoApi = createApi({
  reducerPath: "demoApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["DemoLinks", "DemoLinkMembers"],
  endpoints: (builder) => ({
    // デモリンクの入口: トークンを DRF Token に交換する（認証不要）
    createDemoSession: builder.mutation<DemoSessionResponse, { token: string }>({
      query: ({ token }) => ({
        url: "/api/demo/session",
        method: "POST",
        body: { token },
      }),
    }),

    getDemoLinks: builder.query<GetDemoLinksResponse, void>({
      query: () => "/api/admin/demo-links",
      providesTags: [{ type: "DemoLinks", id: "LIST" }],
    }),

    getDemoLinkMembers: builder.query<GetDemoLinkMembersResponse, string>({
      query: (projectId) => `/api/admin/demo-links/members?project_id=${projectId}`,
      providesTags: (_result, _error, projectId) => [
        { type: "DemoLinkMembers", id: projectId },
      ],
    }),

    // 発行・失効・削除はメンバーの is_demo 状態も変えるため、
    // メンバー一覧（「デモ利用中」表示）ごと無効化して取り直す。
    createDemoLink: builder.mutation<CreateDemoLinkResponse, CreateDemoLinkParams>({
      query: (body) => ({
        url: "/api/admin/demo-links",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "DemoLinks", id: "LIST" }, "DemoLinkMembers"],
    }),

    updateDemoLink: builder.mutation<CreateDemoLinkResponse, UpdateDemoLinkParams>({
      query: ({ demo_link_id, ...body }) => ({
        url: `/api/admin/demo-links/${demo_link_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "DemoLinks", id: "LIST" }, "DemoLinkMembers"],
    }),

    deleteDemoLink: builder.mutation<{ status: string }, string>({
      query: (demoLinkId) => ({
        url: `/api/admin/demo-links/${demoLinkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "DemoLinks", id: "LIST" }, "DemoLinkMembers"],
    }),
  }),
});

export const {
  useCreateDemoSessionMutation,
  useGetDemoLinksQuery,
  useGetDemoLinkMembersQuery,
  useCreateDemoLinkMutation,
  useUpdateDemoLinkMutation,
  useDeleteDemoLinkMutation,
} = demoApi;
